import {
  computeOpenDebtRows,
  isUnassignedDebtOwnerKey,
  localTodayIso,
  sumOpenDebtRowTotals,
  type CashbookDebtEntryRecord,
  type CashbookSettings,
  type OpenDebtRow
} from '@finvox/shared/cashbook';
import { format, parseISO, type Locale } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CashbookOwnerOpenDebtsDialog } from '@/components/cashbook/CashbookOwnerOpenDebtsDialog';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { MoneyDisplay } from '@/components/ui/MoneyDisplay';

type Props = {
  open: boolean;
  onClose: () => void;
  settings: CashbookSettings;
  debtEntries: CashbookDebtEntryRecord[];
  asOfDate?: string;
};

function ownerLabel(row: OpenDebtRow, t: (key: string) => string): string {
  if (isUnassignedDebtOwnerKey(row.ownerKey)) {
    return t('cashbook.debts.unassignedOpeningDebt');
  }
  return row.ownerKey;
}

export function CashbookDebtsDetailDialog({
  open,
  onClose,
  settings,
  debtEntries,
  asOfDate = localTodayIso()
}: Props) {
  const { t, i18n } = useTranslation();
  const locale: Locale = i18n.language.startsWith('ar') ? ar : enUS;
  const [ownerKey, setOwnerKey] = useState<string | null>(null);

  const rows = useMemo(
    () => computeOpenDebtRows(settings, debtEntries, asOfDate),
    [settings, debtEntries, asOfDate]
  );
  const totals = useMemo(() => sumOpenDebtRowTotals(rows), [rows]);

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cashbook-debts-detail-title"
      >
        <Card className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden p-6 shadow-lg">
          <h2 id="cashbook-debts-detail-title" className="shrink-0 text-lg font-semibold">
            {t('cashbook.debts.detailTitle')}
          </h2>

          {rows.length === 0 ? (
            <p className="mt-4 text-sm text-[var(--fv-text-muted)]">{t('cashbook.debts.detailEmpty')}</p>
          ) : (
            <div className="mt-4 min-h-0 flex-1 overflow-auto rounded-lg border border-[var(--fv-border)]">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 z-[1] text-[var(--fv-text-muted)]">
                  <tr>
                    <th className="bg-[var(--fv-bg)] px-3 py-2 text-start font-medium">{t('cashbook.debts.creditor')}</th>
                    <th className="bg-[var(--fv-bg)] px-3 py-2 text-start font-medium">{t('cashbook.colDate')}</th>
                    <th className="bg-[var(--fv-bg)] px-3 py-2 text-start font-medium">{t('cashbook.category')}</th>
                    <th className="bg-[var(--fv-bg)] px-3 py-2 text-start font-medium">{t('cashbook.memo')}</th>
                    <th className="bg-[var(--fv-bg)] px-3 py-2 text-end font-medium">{t('cashbook.debts.colOriginal')}</th>
                    <th className="bg-[var(--fv-bg)] px-3 py-2 text-end font-medium">{t('cashbook.debts.colRemaining')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--fv-border)]">
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          className="text-start font-medium text-[var(--fv-accent)] underline-offset-2 hover:underline"
                          onClick={() => setOwnerKey(row.ownerKey)}
                          aria-label={t('cashbook.debts.ownerDetailAria', {
                            owner: ownerLabel(row, t)
                          })}
                        >
                          {ownerLabel(row, t)}
                        </button>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {format(parseISO(row.date), 'PP', { locale })}
                      </td>
                      <td className="px-3 py-2">
                        {row.source === 'opening_debt'
                          ? t('cashbook.debts.openingRow')
                          : t(`cashbook.debts.categories.${row.categoryKey}`, {
                              defaultValue: row.categoryKey
                            })}
                      </td>
                      <td className="px-3 py-2 text-[var(--fv-text-muted)]">{row.memo ?? '—'}</td>
                      <td className="px-3 py-2 text-end tabular-nums">
                        <MoneyDisplay value={row.originalAmount} />
                      </td>
                      <td className="px-3 py-2 text-end font-medium tabular-nums">
                        <MoneyDisplay value={row.remainingAmount} />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="sticky bottom-0 z-[1]">
                  <tr className="border-t border-[var(--fv-border)] font-medium shadow-[0_-6px_12px_-8px_rgb(15_23_42_/_0.18)]">
                    <td className="bg-[var(--fv-bg)] px-3 py-2" colSpan={4}>
                      {t('cashbook.debts.totals')}
                    </td>
                    <td className="bg-[var(--fv-bg)] px-3 py-2 text-end tabular-nums">
                      <MoneyDisplay value={totals.originalAmount} />
                    </td>
                    <td className="bg-[var(--fv-bg)] px-3 py-2 text-end tabular-nums">
                      <MoneyDisplay value={totals.remainingAmount} />
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          <div className="mt-6 flex shrink-0 justify-end">
            <Button
              variant="secondary"
              onClick={() => {
                setOwnerKey(null);
                onClose();
              }}
            >
              {t('common.cancel')}
            </Button>
          </div>
        </Card>
      </div>

      {ownerKey ? (
        <CashbookOwnerOpenDebtsDialog
          open
          onClose={() => setOwnerKey(null)}
          ownerKey={ownerKey}
          settings={settings}
          debtEntries={debtEntries}
          asOfDate={asOfDate}
        />
      ) : null}
    </>
  );
}
